import { create } from 'zustand';
import { i18n } from '../i18n';
import { getMeAbsences } from '../api/generated/me-absences/me-absences';
import { getEmployeeVacation } from '../api/generated/employee-vacation/employee-vacation';
import type { FacilityFlowCoreModulesPlanningDtosVacationVacationBalanceDto } from '../api/generated/models';
import type { VacationBalanceDto } from '../types';
import { useToastStore } from './useToastStore';

interface VacationState {
  /** Urlaubskonto des angemeldeten Mitarbeiters. */
  myBalance: VacationBalanceDto | null;
  /** Urlaubskonten je Mitarbeiter (Verwaltungssicht). */
  balancesByEmployee: Record<string, VacationBalanceDto>;
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchMyBalance: (referenceDate?: string) => Promise<void>;
  fetchEmployeeBalance: (employeeId: string, referenceDate?: string) => Promise<void>;
  adjustEmployeeBalance: (
    employeeId: string,
    adjustmentDays: number,
    note: string,
    referenceDate?: string,
  ) => Promise<void>;
}

const meApi = getMeAbsences();
const employeeVacationApi = getEmployeeVacation();

const t = (key: string) => i18n.t(key, { ns: 'absences' }) as string;

const toBalance = (
  dto: FacilityFlowCoreModulesPlanningDtosVacationVacationBalanceDto,
): VacationBalanceDto => ({
  employeeId: dto.employeeId ?? '',
  vacationYearStart: dto.vacationYearStart ?? '',
  vacationYearEnd: dto.vacationYearEnd ?? '',
  entitledDays: dto.entitledDays ?? 0,
  carriedOverDays: dto.carriedOverDays ?? 0,
  carryOverExpiresAt: dto.carryOverExpiresAt ?? null,
  carryOverExpired: dto.carryOverExpired ?? false,
  manualAdjustmentDays: dto.manualAdjustmentDays ?? 0,
  adjustmentNote: dto.adjustmentNote ?? null,
  usedDays: dto.usedDays ?? 0,
  pendingDays: dto.pendingDays ?? 0,
  remainingDays: dto.remainingDays ?? 0,
  entitledDaysUnrounded: dto.entitledDaysUnrounded ?? null,
  manualAdjustmentDaysUnrounded: dto.manualAdjustmentDaysUnrounded ?? null,
  remainingDaysUnrounded: dto.remainingDaysUnrounded ?? null,
});

export const useVacationStore = create<VacationState>((set, get) => ({
  myBalance: null,
  balancesByEmployee: {},
  loading: false,
  saving: false,
  error: null,

  fetchMyBalance: async (referenceDate) => {
    set({ loading: true, error: null });
    try {
      const response = await meApi.getApiV1MeAbsencesVacationBalance(
        referenceDate ? { referenceDate } : undefined,
      );
      set({ myBalance: toBalance(response), loading: false });
    } catch {
      // Fehler-State statt Toast — die Seite zeigt das Konto dann einfach nicht an.
      set({ error: 'errors.balanceFetchFailed', loading: false });
    }
  },

  fetchEmployeeBalance: async (employeeId, referenceDate) => {
    set({ loading: true, error: null });
    try {
      const response = await employeeVacationApi.getApiV1EmployeesEmployeeIdVacationBalance(
        employeeId,
        referenceDate ? { referenceDate } : undefined,
      );
      set((state) => ({
        balancesByEmployee: { ...state.balancesByEmployee, [employeeId]: toBalance(response) },
        loading: false,
      }));
    } catch {
      set({ error: 'errors.balanceFetchFailed', loading: false });
    }
  },

  adjustEmployeeBalance: async (employeeId, adjustmentDays, note, referenceDate) => {
    set({ saving: true, error: null });
    try {
      const response =
        await employeeVacationApi.postApiV1EmployeesEmployeeIdVacationEntitlementsCurrentAdjust(
          employeeId,
          { adjustmentDays, note, referenceDate: referenceDate ?? null },
        );

      set((state) => ({
        balancesByEmployee: { ...state.balancesByEmployee, [employeeId]: toBalance(response) },
        saving: false,
      }));

      // Eigenes Konto mitziehen, falls die Verwaltung sich selbst korrigiert hat.
      if (get().myBalance?.employeeId === employeeId) {
        await get().fetchMyBalance(referenceDate);
      }

      useToastStore.getState().addToast(t('vacation.adjusted'), 'success');
    } catch (error) {
      set({ saving: false, error: 'errors.adjustFailed' });
      throw error;
    }
  },
}));
